import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from './Checkbox.scss?inline';

@customElement('app-checkbox')
export class CheckboxComponent extends LitElement {
  @property({ type: Boolean, reflect: true }) em_checked = false;
  @property({ type: Boolean, reflect: true }) em_indeterminate = false;
  @property({ type: Boolean, reflect: true }) em_disabled = false;

  /** Tamaño total del checkbox en px */
  @property({ type: Number, reflect: true }) em_size = 18;

  /** Radio de borde del cuadrado exterior (px) */
  @property({ type: Number }) em_borderRadius = 3;

  /** Grosor del borde exterior (px) */
  @property({ type: Number }) em_borderWidth = 2;

  @state() private _hovered = false;

  static styles = [unsafeCSS(styles)];

  private _handleClick() {
    if (this.em_disabled) return;
    this.em_indeterminate = false;
    this.em_checked = !this.em_checked;
    this.dispatchEvent(new CustomEvent('checkbox-toggle', {
      detail: { checked: this.em_checked },
      bubbles: true,
      composed: true
    }));
  }

  private _handleKeydown(e: KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._handleClick();
    }
  }

  render() {
    const size = Math.max(12, this.em_size);
    const bw = Math.max(1, Math.min(this.em_borderWidth, size / 4));
    const radius = Math.max(0, Math.min(this.em_borderRadius, (size - bw) / 2));

    // El stroke se centra en el borde, desplaza para que quede dentro del viewBox
    const outerX = bw / 2;
    const outerY = bw / 2;
    const outerSize = size - bw;

    // Gap proporcional para que el espacio interior se mantenga congruente al escalar
    const gap = Math.max(2, Math.round(size * 0.16));
    const innerX = gap;
    const innerY = gap;
    const innerSize = size - gap * 2;

    // Rectángulo horizontal para indeterminate
    const rectH = Math.max(3, Math.round(innerSize * 0.38));
    const rectY = (size - rectH) / 2;

    const isActive = this.em_checked || this.em_indeterminate;
    const strokeColor = (isActive || this._hovered)
      ? 'var(--checkbox-color)'
      : 'var(--checkbox-border)';

    return html`
      <svg
        width="${size}"
        height="${size}"
        viewBox="0 0 ${size} ${size}"
        class="${this.em_disabled ? 'is-disabled' : ''}"
        role="checkbox"
        aria-checked="${this.em_indeterminate ? 'mixed' : String(this.em_checked)}"
        tabindex="${this.em_disabled ? '-1' : '0'}"
        @click="${this._handleClick}"
        @keydown="${this._handleKeydown}"
        @mouseenter="${() => { if (!this.em_disabled) this._hovered = true; }}"
        @mouseleave="${() => { this._hovered = false; }}"
      >
        <!-- Borde exterior -->
        <rect
          x="${outerX}"
          y="${outerY}"
          width="${outerSize}"
          height="${outerSize}"
          rx="${radius}"
          fill="white"
          stroke="${strokeColor}"
          stroke-width="${bw}"
        />
        <!-- Checked: cuadrado interior -->
        <rect
          x="${innerX}"
          y="${innerY}"
          width="${innerSize}"
          height="${innerSize}"
          rx="${Math.max(0, radius - Math.max(1, Math.round(size * 0.04)))}"
          fill="var(--checkbox-color)"
          style="${this.em_checked && !this.em_indeterminate ? '' : 'display:none'}"
        />
        <!-- Indeterminate: rectángulo horizontal -->
        <rect
          x="${innerX}"
          y="${rectY}"
          width="${innerSize}"
          height="${rectH}"
          rx="2"
          fill="var(--checkbox-color)"
          style="${this.em_indeterminate ? '' : 'display:none'}"
        />
      </svg>
    `;
  }
}

// Declaraciones globales para TypeScript

declare global {
  interface HTMLElementTagNameMap {
    'app-checkbox': CheckboxComponent;
  }

  interface HTMLElementEventMap {
    'checkbox-toggle': CustomEvent<{ checked: boolean }>;
  }
}
